import React, { useState, useEffect, useRef } from "react";
import MobileMenu from "./MobileMenu";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import api from "../api";
import Cookies from "js-cookie";

function Header({ cartItems, grandTotal, removeItemFromCart, wishlistCount }) {
  const [isSticky, setIsSticky] = useState(false);
  const location = useLocation();
  const [isMobileMenuVisible, setIsMobileMenuVisible] = useState(false);
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [metaData, setMetaData] = useState({});
  const [settings, setSettings] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const searchRef = useRef(null);
  const navigate = useNavigate();

  const handleSubmit = (e) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      window.location.href = `/search?query=${encodeURIComponent(searchTerm)}`;
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await api.get("/business-settings");
        setSettings(response.data.data.data);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const fetchCategoriesAndSubcategories = async () => {
      try {
        const response = await api.get("/categories");
        const fetchedCategories = response.data.data;
        const categoriesWithSubcategories = await Promise.all(
          fetchedCategories.map(async (category) => {
            const subResponse = await api.get(`/sub-categories/${category.id}`);
            return {
              ...category,
              subcategories: subResponse.data.data,
            };
          })
        );
        setCategories(categoriesWithSubcategories);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };
    fetchCategoriesAndSubcategories();
  }, []);

  useEffect(() => {
    if (Array.isArray(settings)) {
      const meta = {};
      settings.forEach((setting) => {
        if (setting.type === "header_logo_path") meta.header_logo_path = setting.value;
        if (setting.type === "site_icon_path") meta.site_icon_path = setting.value;
        if (setting.type === "website_name") meta.website_name = setting.value;
        if (setting.type === "header_top") meta.header_top = setting.value;
        if (setting.type === "website_topbar_content") meta.website_topbar_content = setting.value;
        if (setting.type === "topbar_link_name") meta.topbar_link_name = setting.value;
        if (setting.type === "topbar_banner_link") meta.topbar_banner_link = setting.value;
        if (setting.type === "contact_email") meta.contact_email = setting.value;
        if (setting.type === "contact_phone") meta.contact_phone = setting.value;
        if (setting.type === "instagram_link") meta.instagram_link = setting.value;
        if (setting.type === "facebook_link") meta.facebook_link = setting.value;
        if (setting.type === "whatsapp_link") meta.whatsapp_link = setting.value;
      });
      setMetaData(meta);
    }
  }, [settings]);

  useEffect(() => {
    const handleScroll = () => setIsSticky(window.scrollY > 100);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuVisible((prev) => !prev);
    document.body.classList.toggle("mobile-menu-visible", !isMobileMenuVisible);
  };

  const toggleSearchPopup = () => {
    setIsSearchActive((prev) => !prev);
    document.body.classList.toggle("mobile-search-active", !isSearchActive);
  };

  const menuItems = [
    { title: "Products", link: "/products", },
    { title: "Services", link: "/services" },
    { title: "Gifting", link: "/gifting", },
    { title: "Bulk Deals", link: "/bulk-deals", },
    { title: "Corporate", link: "/corporate", },
    // { title: "+91 9821495696", link: "tel:+919821495696", },
  ];

  const renderSubMenu = (subMenu) => {
    return (
      <ul className="dropdown-menu">
        {subMenu.map((subItem, i) => (
          <li key={i}>
            <Link to={subItem.link}>{subItem.title}</Link>
          </li>
        ))}
      </ul>
    );
  };

  const renderMenuItems = () => {
    return menuItems.map((item, index) => (
      <li
        key={index}
        className={item.subMenu && item.subMenu.length > 0 ? "dropdown" : ""}
      >
        <Link to={item.link}>{item.title}</Link>
        {item.subMenu && item.subMenu.length > 0 && renderSubMenu(item.subMenu)}
        {item.subMenu && item.subMenu.length > 0 && (
          <div className="dropdown-btn">
            <i className="fa-solid fa-angle-down"></i>
          </div>
        )}
      </li>
    ));
  };


  useEffect(() => {
    const searchProducts = async () => {
      if (searchTerm.length >= 3) {
        try {
          const response = await api.get(`/products/search?name=${searchTerm}`);
          setResults(response.data.data);
          setShowResults(true);
        } catch (error) {
          console.error("Error fetching search results", error);
        }
      } else {
        setShowResults(false);
      }
    };
    const debounceSearch = setTimeout(() => {
      searchProducts();
    }, 500);
    return () => clearTimeout(debounceSearch);
  }, [searchTerm]);

  const handleKeyUp = (e) => setSearchTerm(e.target.value);

  const handleClick = (slug) => {
    setShowResults(false);
    navigate(`/products/${slug}`);
  };

  const handleClickOutside = (event) => {
    if (
      searchRef.current &&
      !searchRef.current.contains(event.target) &&
      !event.target.closest(".search-results")
    ) {
      setShowResults(false);
    }
  };

  const handleFocus = () => {
    if (results.length > 0) {
      setShowResults(true);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Elley Decor</title>
        <meta name="keywords" content="Elley Decor" />
        <meta name="title" content="Elley Decor" />
        <meta name="description" content="Elley Decor" />
        <meta name="csrf-token" content={metaData.csrf_token} />
      </Helmet>

      {/* Header Desktop */}
      <header
        id="header"
        className={`main-header 
          ${location.pathname === "/" && !isSticky ? "transparent-header" : ""} 
          ${isSticky ? "fixed-header" : ""}`}
      >
        <div className="header-row d-none d-md-flex align-items-center justify-content-between">
          <Link to="/" className="custom-logo-link">
            <span className="custom-header-logo"
            style={{ fontFamily: 'IbarraRealNova, sans-serif' }}>
              {metaData.website_name}
            </span>
          </Link>


          {/* Navigation */}
          <nav className="custom-nav desktop-nav">
            <ul className="custom-nav-list">
              {renderMenuItems()}
              {metaData.contact_phone && (
                <div className="elly-footer-phone d-flex align-items-center me-4">
                  <span
                    className="elly-header-phone-icon"
                    style={{ fontSize: 14, marginRight: 8 }}
                  >
                    <i className="fas fa-phone-alt" style={{color: isSticky ? "#02132f" : "#fff",}}></i>
                  </span>
                  <a
                    href={`tel:${metaData.contact_phone}`}
                    className="elly-footer-phone-number"
                    style={{color: isSticky ? "#000" : "#fff",fontWeight:400, textDecoration: "none" }}
                  >
                    {"+91 "+metaData.contact_phone}
                  </a>
                </div>
              )}
              {(metaData.instagram_link || metaData.facebook_link || metaData.contact_phone) && (
                <li className="social-links-group">
                  {/* <span className="social-title">Social Links</span> */}
                  <div className="social-icons  d-flex gap-1">
                    {metaData.instagram_link && (
                      <a
                        href={metaData.instagram_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="social-icon insta"
                        style={{ fontSize: 20 }}
                      >
                        <i className="fab fa-instagram"></i>
                      </a>
                    )}

                    {metaData.facebook_link && (
                      <a
                        href={metaData.facebook_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="social-icon face"
                        style={{ fontSize: 20 }}
                      >
                        <i className="fab fa-facebook"></i>
                      </a>
                    )}

                    {metaData.contact_phone && (
                      <a
                        href={`https://wa.me/${metaData.contact_phone}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="social-icon whats"
                        style={{ fontSize: 20 }}
                      >
                        <i className="fab fa-whatsapp"></i>
                      </a>
                    )}
                  </div>
                </li>
              )}
            </ul>
          </nav>
        </div>

        {/* Mobile header */}
        <div className="mobile-header pt-4">
          <div className="container-fluid">
            <div className="row align-items-center">
              <div className="col-3 left-box">
                <div className="mobile-nav-toggler" onClick={toggleMobileMenu}>
                  <span className="icon lnr-icon-bars"></span>
                </div>
              </div>
              <div className="col-9 logo-box">
                <div className="logo">
                  {/* <Link to="/">
                    <img
                      src={metaData.header_logo_path}
                      alt={metaData.website_name}
                    />
                  </Link> */}
                  <Link to="/" className="custom-logo-link">
                    <span className="custom-header-logo ms-4">
                      {metaData.website_name}
                    </span>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>

        <MobileMenu
          isVisible={isMobileMenuVisible}
          toggleMenu={toggleMobileMenu}
          menuItems={menuItems}
        />
      </header>

      {/* Spacer only if not home */}
      {location.pathname !== "/" && <div style={{ height: 60 }} />}
    </>
  );
}

export default Header;